export interface LibraryFile {
  Id: string;
  Title: string;
  Extension?: string;
  Filesize?: number;
}

export interface LibraryFolder {
  Id: string;
  Title: string;
  Files: LibraryFile[];
  folders?: LibraryFolder[];
}

export interface LibraryPortfolio {
  id: string;
  title: string;
  clientId?: string;
  /** Private, Public or Global. */
  access?: string;
  /** True when the signed-in user may upload into this portfolio. */
  canUpload?: boolean;
  folders: LibraryFolder[];
}

export interface LibraryLink {
  id?: string;
  /** Cosmos library file document id (not the FileLink id). */
  fileId?: string;
  fileName: string;
  portfolioId: string;
  folderId: string;
  type: string;
  areaId: string;
  questionId: string;
}

export interface LibraryFileCollection {
  Files: LibraryFile[];
}

export type LibraryFileRef = {
  portfolioId: string;
  folderId: string;
  fileName: string;
  /** Cosmos library file document id — required for MCP retrieve_library_files_content. */
  fileId?: string;
};

/** Per-file failure detail returned by CreateFileLinks. */
export type CreateFileLinksFailure = {
  fileName?: string;
  portfolioId?: string;
  folderId?: string;
  fileId?: string;
  error?: string;
};

/** Response body from the CreateFileLinks endpoint. */
export type CreateFileLinksResult = {
  created: number;
  failed: number;
  failures: CreateFileLinksFailure[];
  links?: LibraryLink[];
};

export function flattenLibraryFolders(folders: LibraryFolder[] | undefined): LibraryFolder[] {
  return (folders ?? []).flatMap((folder) => [folder, ...flattenLibraryFolders(folder.folders)]);
}

export function findLibraryFolder(folders: LibraryFolder[] | undefined, folderId: string): LibraryFolder | undefined {
  for (const folder of folders ?? []) {
    if (folder.Id === folderId) return folder;
    const nested = findLibraryFolder(folder.folders, folderId);
    if (nested) return nested;
  }
  return undefined;
}

export function libraryFileRefKey(ref: LibraryFileRef): string {
  return `${ref.portfolioId}:${ref.folderId}:${ref.fileName}`;
}

export function dedupeLibraryFileRefs<T extends LibraryFileRef>(refs: T[]): T[] {
  const seen = new Set<string>();
  return refs.filter((ref) => {
    const key = libraryFileRefKey(ref);
    if (seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });
}
